import { createApi } from 'ReduxHelpers';
import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import {
    type ProjectRequirementIdentifiersResponse,
    type ProjectVariantLabelsResponse,
    type GradersResponse,
    type UpdateRequirementIdentifierGradersParams,
    type UpdateRequirementIdentifierGradersResponse,
    type ProjectSubmission,
    type ProjectSubmissionsResponse,
    type SubmissionsQueryParams,
    type ClaimResponse,
    type EvaluationModalDataParams,
    type EvaluationModalDataResponse,
    type NeedsReviewCountResponse,
    type SaoCheckSummaryResponse,
    type SaoCheckDetailResponse,
    type SaoChunkMatchesResponse,
    type SaoSentenceReviewData,
    type UpdateSentenceReviewParams,
    type SaoPrivateNotesResponse,
    type UpdateSaoPrivateNotesParams,
    type TriggerSaoCheckResponse,
} from './gradingPortalApi.types';

enum TagType {
    ProjectRequirementIdentifiers = 'ProjectRequirementIdentifiers',
    Graders = 'Graders',
    ProjectSubmission = 'ProjectSubmission',
    Claim = 'Claim',
    SaoCheck = 'SaoCheck',
}

export const gradingPortalApi = createApi('GradingPortalApi', {
    ...getBackRoyalApiBaseConfig('GradingPortalApi'),
    tagTypes: Object.values(TagType),
    // oxlint-disable-next-line max-lines-per-function
    endpoints: builder => ({
        // ====================================================================
        // Admin endpoints - Project Requirement Identifiers & Graders
        // ====================================================================

        listProjectRequirementIdentifiers: builder.query<ProjectRequirementIdentifiersResponse, void>({
            query: () => ({ url: 'grading_portal/admin/list_project_requirement_identifiers.json' }),
            providesTags: [TagType.ProjectRequirementIdentifiers],
        }),

        listProjectVariantLabels: builder.query<ProjectVariantLabelsResponse, { context: 'admin' | 'grader' }>({
            query: ({ context }) => ({ url: `grading_portal/${context}/list_project_variant_labels.json` }),
        }),

        listGraders: builder.query<GradersResponse, void>({
            query: () => ({ url: 'grading_portal/admin/list_graders.json' }),
            providesTags: [TagType.Graders],
        }),

        updateRequirementIdentifierGraders: builder.mutation<
            UpdateRequirementIdentifierGradersResponse,
            UpdateRequirementIdentifierGradersParams
        >({
            query: ({ requirementIdentifier, graderIds, expectedGraderIds }) => ({
                url: 'grading_portal/admin/update_requirement_identifier_graders.json',
                method: 'PUT',
                body: { requirementIdentifier, graderIds, expectedGraderIds },
            }),
            invalidatesTags: [TagType.ProjectRequirementIdentifiers],
        }),

        // ====================================================================
        // Admin endpoints - Project Submissions
        // ====================================================================

        listProjectSubmissions: builder.query<ProjectSubmissionsResponse, SubmissionsQueryParams>({
            query: ({ page = 1, perPage = 25, sort = 'urgency_score', direction = 'asc', filters = {} }) => ({
                url: 'grading_portal/admin/list_project_submissions.json',
                method: 'GET',
                params: {
                    page,
                    perPage,
                    sort,
                    direction,
                    filters,
                },
            }),
            providesTags: result =>
                result?.projectSubmissions
                    ? [
                          ...result.projectSubmissions.map(s => ({
                              type: TagType.ProjectSubmission,
                              id: s.id,
                          })),
                          { type: TagType.ProjectSubmission, id: 'LIST' },
                      ]
                    : [{ type: TagType.ProjectSubmission, id: 'LIST' }],
        }),

        getProjectSubmission: builder.query<ProjectSubmission | null, string>({
            query: submissionId => ({
                url: 'grading_portal/admin/list_project_submissions.json',
                method: 'GET',
                params: {
                    perPage: 1,
                    filters: { id: submissionId },
                },
            }),
            transformResponse: (response: ProjectSubmissionsResponse) => response.projectSubmissions[0] ?? null,
        }),

        getEvaluationModalData: builder.query<EvaluationModalDataResponse, EvaluationModalDataParams>({
            query: ({ userId, cohortId, requirementIdentifier }) => ({
                url: 'grading_portal/admin/evaluation_modal_data.json',
                method: 'GET',
                params: {
                    user_id: userId,
                    cohort_id: cohortId,
                    requirement_identifier: requirementIdentifier,
                },
            }),
        }),

        getNeedsReviewCount: builder.query<NeedsReviewCountResponse, void>({
            query: () => ({ url: 'grading_portal/admin/needs_review_count.json' }),
            providesTags: [{ type: TagType.ProjectSubmission, id: 'NEEDS_REVIEW_COUNT' }],
        }),

        // ====================================================================
        // Claims endpoints
        // ====================================================================

        createClaim: builder.mutation<
            ClaimResponse,
            { project_submission_id: string; grader_id?: string; admin_assignment?: boolean }
        >({
            query: ({ project_submission_id, grader_id, admin_assignment }) => ({
                url: 'grading_portal/claims.json',
                method: 'POST',
                body: { project_submission_id, grader_id, admin_assignment },
            }),
            invalidatesTags: (_result, _error, { project_submission_id }) => [
                { type: TagType.ProjectSubmission, id: project_submission_id },
                { type: TagType.ProjectSubmission, id: 'LIST' },
                { type: TagType.ProjectSubmission, id: 'UNCLAIMED_LIST' },
                { type: TagType.ProjectSubmission, id: 'CLAIMED_LIST' },
                { type: TagType.Claim, id: 'LIST' },
            ],
        }),

        updateClaim: builder.mutation<ClaimResponse, { id: string; status: string }>({
            query: ({ id, status }) => ({
                url: `grading_portal/claims/${id}.json`,
                method: 'PUT',
                body: { status },
            }),
            invalidatesTags: [
                { type: TagType.ProjectSubmission, id: 'LIST' },
                { type: TagType.ProjectSubmission, id: 'UNCLAIMED_LIST' },
                { type: TagType.ProjectSubmission, id: 'CLAIMED_LIST' },
                { type: TagType.Claim, id: 'LIST' },
            ],
        }),

        deleteClaim: builder.mutation<ClaimResponse, string>({
            query: id => ({
                url: `grading_portal/claims/${id}.json`,
                method: 'DELETE',
            }),
            invalidatesTags: [
                { type: TagType.ProjectSubmission, id: 'LIST' },
                { type: TagType.ProjectSubmission, id: 'UNCLAIMED_LIST' },
                { type: TagType.ProjectSubmission, id: 'CLAIMED_LIST' },
                { type: TagType.Claim, id: 'LIST' },
            ],
        }),

        // ====================================================================
        // Grader endpoints (for "My Grading Tasks" tab)
        // ====================================================================

        listUnclaimedSubmissions: builder.query<ProjectSubmissionsResponse, SubmissionsQueryParams>({
            query: ({ page = 1, perPage = 25, sort = 'urgency_score', direction = 'asc', filters = {} }) => ({
                url: 'grading_portal/grader/list_unclaimed_project_submissions.json',
                method: 'GET',
                params: { page, perPage, sort, direction, filters },
            }),
            providesTags: [{ type: TagType.ProjectSubmission, id: 'UNCLAIMED_LIST' }],
        }),

        listClaimedSubmissions: builder.query<ProjectSubmissionsResponse, SubmissionsQueryParams>({
            query: ({ page = 1, perPage = 25, sort = 'urgency_score', direction = 'asc', filters = {} }) => ({
                url: 'grading_portal/grader/list_claimed_project_submissions.json',
                method: 'GET',
                params: { page, perPage, sort, direction, filters },
            }),
            providesTags: [{ type: TagType.ProjectSubmission, id: 'CLAIMED_LIST' }],
        }),

        listCompletedSubmissions: builder.query<ProjectSubmissionsResponse, SubmissionsQueryParams>({
            query: ({ page = 1, perPage = 25, sort = 'date_graded', direction = 'desc', filters = {} }) => ({
                url: 'grading_portal/grader/list_completed_project_submissions.json',
                method: 'GET',
                params: { page, perPage, sort, direction, filters },
            }),
            providesTags: [{ type: TagType.ProjectSubmission, id: 'COMPLETED_LIST' }],
        }),

        listAssignedRequirementIdentifiers: builder.query<{ requirementIdentifiers: string[] }, void>({
            query: () => ({ url: 'grading_portal/grader/list_assigned_requirement_identifiers.json' }),
        }),

        getGraderEvaluationModalData: builder.query<EvaluationModalDataResponse, EvaluationModalDataParams>({
            query: ({ userId, cohortId, requirementIdentifier }) => ({
                url: 'grading_portal/grader/evaluation_modal_data.json',
                method: 'GET',
                params: {
                    user_id: userId,
                    cohort_id: cohortId,
                    requirement_identifier: requirementIdentifier,
                },
            }),
        }),

        // ====================================================================
        // SAO Check endpoints (admin-only)
        // ====================================================================

        getSaoCheckSummary: builder.query<SaoCheckSummaryResponse, string>({
            query: submissionId => ({
                url: 'grading_portal/sao/check_summary.json',
                params: { project_submission_id: submissionId },
            }),
            providesTags: (_result, _error, submissionId) => [{ type: TagType.SaoCheck, id: submissionId }],
        }),

        getSaoCheckDetail: builder.query<SaoCheckDetailResponse, string>({
            query: sessionId => ({
                url: `grading_portal/sao/check_detail/${sessionId}.json`,
            }),
            providesTags: (_result, _error, sessionId) => [{ type: TagType.SaoCheck, id: sessionId }],
        }),

        getChunkMatches: builder.query<SaoChunkMatchesResponse, string>({
            query: resultId => ({
                url: `grading_portal/sao/chunk_matches/${resultId}.json`,
            }),
            providesTags: (_result, _error, resultId) => [{ type: TagType.SaoCheck, id: resultId }],
        }),

        updateSentenceReview: builder.mutation<SaoSentenceReviewData, UpdateSentenceReviewParams>({
            query: ({ resultId, ...body }) => ({
                url: `grading_portal/sao/sentence_review/${resultId}.json`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_result, _error, { resultId }) => [{ type: TagType.SaoCheck, id: resultId }],
        }),

        updateSaoPrivateNotes: builder.mutation<SaoPrivateNotesResponse, UpdateSaoPrivateNotesParams>({
            query: ({ submissionId, privateNotes }) => ({
                url: `grading_portal/sao/private_notes/${submissionId}.json`,
                method: 'PUT',
                body: { private_notes: privateNotes },
            }),
            // Refresh the cached check_detail (tagged by session) so a reopened modal shows the saved value.
            invalidatesTags: (_result, _error, { sessionId }) => [{ type: TagType.SaoCheck, id: sessionId }],
        }),

        triggerSaoCheck: builder.mutation<TriggerSaoCheckResponse, string>({
            query: submissionId => ({
                url: 'grading_portal/sao/trigger_check.json',
                method: 'POST',
                body: { project_submission_id: submissionId },
            }),
            invalidatesTags: (_result, _error, submissionId) => [
                { type: TagType.SaoCheck, id: submissionId },
                { type: TagType.ProjectSubmission, id: submissionId },
            ],
        }),
    }),
});

export const {
    // Admin - Project Requirement Identifiers, Variant Labels & Graders
    useListProjectRequirementIdentifiersQuery,
    useListProjectVariantLabelsQuery,
    useListGradersQuery,
    useUpdateRequirementIdentifierGradersMutation,
    // Admin - Project Submissions
    useListProjectSubmissionsQuery,
    useLazyGetProjectSubmissionQuery,
    useGetEvaluationModalDataQuery,
    useLazyGetEvaluationModalDataQuery,
    // Admin - Needs Review Count
    useGetNeedsReviewCountQuery,
    // Claims
    useCreateClaimMutation,
    useUpdateClaimMutation,
    useDeleteClaimMutation,
    // Grader
    useListUnclaimedSubmissionsQuery,
    useListClaimedSubmissionsQuery,
    useListCompletedSubmissionsQuery,
    useLazyListCompletedSubmissionsQuery,
    useListAssignedRequirementIdentifiersQuery,
    useGetGraderEvaluationModalDataQuery,
    useLazyGetGraderEvaluationModalDataQuery,
    // SAO Check
    useGetSaoCheckSummaryQuery,
    useLazyGetSaoCheckSummaryQuery,
    useGetSaoCheckDetailQuery,
    useLazyGetSaoCheckDetailQuery,
    useGetChunkMatchesQuery,
    useLazyGetChunkMatchesQuery,
    useUpdateSentenceReviewMutation,
    useUpdateSaoPrivateNotesMutation,
    useTriggerSaoCheckMutation,
} = gradingPortalApi;

export default gradingPortalApi;

/**
 * Creates an action to invalidate all ProjectSubmission-related cache tags.
 * Use this after saving changes in the evaluation modal to refresh all grading portal data.
 */
export function invalidateAllProjectSubmissionTags(submissionId?: string) {
    const tags = [
        { type: TagType.ProjectSubmission, id: 'LIST' },
        { type: TagType.ProjectSubmission, id: 'NEEDS_REVIEW_COUNT' },
        { type: TagType.ProjectSubmission, id: 'CLAIMED_LIST' },
        { type: TagType.ProjectSubmission, id: 'UNCLAIMED_LIST' },
        { type: TagType.ProjectSubmission, id: 'COMPLETED_LIST' },
    ];
    if (submissionId) {
        tags.push({ type: TagType.ProjectSubmission, id: submissionId });
    }
    return gradingPortalApi.util.invalidateTags(tags);
}

/** Creates an action to invalidate the cached S&O check detail for a session. */
export function invalidateSaoCheckDetailTag(sessionId: string) {
    return gradingPortalApi.util.invalidateTags([{ type: TagType.SaoCheck, id: sessionId }]);
}
